import { useAppSelector } from '../app/typedReduxHooks'
import { usePrescribingLocation } from '../features/locations/hooks/usePrescribingLocation'
import { definedPrescribedItemsSelector } from '../pages/NewScriptPage/ScriptForm/PrescribedItem/PrescribedItemSlice'
import {
  isAuthPhoneRequiredForPrescribedItem,
  isSchedule8Medicine,
  repeatIntervalRequired,
} from '../pages/NewScriptPage/utilities'
import { isEmpty } from 'lodash'
import GoogleAnalyticsTagManager from '../utils/google-analytics/GoogleAnalyticsTagManager'
import { useGAContextData } from '../utils/google-analytics/useGAContextData'

interface ReauthenticationInfo {
  isDone: boolean
}

interface UseValidateDrugsParams {
  reauthenticationInfo: ReauthenticationInfo
  onRequireReauthentication: () => void
}

interface ValidateDrugsResult {
  isValid: boolean
  error: string | null
}

export const useValidateDrugs = ({
  reauthenticationInfo,
  onRequireReauthentication,
}: UseValidateDrugsParams) => {
  const prescriber = useAppSelector((state) => state.prescriber.prescriber)
  const selectedPatient = useAppSelector((state) => state.patientDetail.selectedPatient)
  const { prescribingLocation } = usePrescribingLocation()
  const drugs = useAppSelector(definedPrescribedItemsSelector)
  const { enableS8Medicine: isEnableS8Medicine } =
    useAppSelector((state) => state.organization?.data?.settings) ?? {}
  const GAContextData = useGAContextData()

  const validateDrugs = (): ValidateDrugsResult => {
    // Validate each drug and collect validation results
    const drugsValidationResults = drugs.map((drug) => {
      const { isRPBS, patientInstructions, repeatIntervals, numberOfRepeatsAuthorised } = drug

      // Check if repeat interval is required and valid
      const isRepeatIntervalRequired = repeatIntervalRequired(
        numberOfRepeatsAuthorised,
        prescribingLocation?.state ?? '',
        drug.productDetails
      )
      const hasValidInterval =
        !isRepeatIntervalRequired || (repeatIntervals !== 0 && repeatIntervals !== undefined)

      // Check if treatment use is required and valid
      const requiresTreatment = ['D', 'F', 'E', 'T'].includes(prescriber?.prescriberType || '')
      const hasTreatmentUse = !isEmpty(drug.treatment)
      const isTreatmentValid = !requiresTreatment || hasTreatmentUse

      // Validate RPBS requires DVA number
      const hasInstructions = !isEmpty(patientInstructions)
      const isRPBSValid = !isRPBS || !!selectedPatient?.dva_file_no

      // Determine first validation error (priority order matters)
      let errorMsg = ''
      if (!hasInstructions) {
        errorMsg = 'Please make sure you add instructions to all the drugs.'
      } else if (!isRPBSValid) {
        errorMsg =
          'Please make sure you provide the DVA number when any of selected drug checks the RPBS'
      } else if (!hasValidInterval) {
        errorMsg = 'Please make sure you set minimum repeats interval'
      } else if (!isTreatmentValid) {
        errorMsg = 'Please select a valid treatment use'
      }

      return {
        isValid: hasInstructions && isRPBSValid && hasValidInterval && isTreatmentValid,
        isSchedule8Medicine: isSchedule8Medicine(drug),
        error: errorMsg || null,
      }
    })

    const isEveryDrugValid = drugsValidationResults.every(({ isValid }) => isValid)
    const hasSchedule8Medicine = drugsValidationResults.some(
      ({ isSchedule8Medicine }) => isSchedule8Medicine
    )
    if (hasSchedule8Medicine && !reauthenticationInfo.isDone && isEnableS8Medicine) {
      GoogleAnalyticsTagManager.sendPrescriptionCreationErrorEvent({
        ...GAContextData,
        error: 'Require reauthentication',
      })

      onRequireReauthentication()
      return {
        isValid: false,
        error: null,
      }
    }

    if (!isEveryDrugValid) {
      const firstInvalidDrug = drugsValidationResults.find(({ isValid }) => !isValid)
      return {
        isValid: false,
        error: firstInvalidDrug?.error || 'Unknown validation error',
      }
    }

    const isAllDrugsPhoneAuthValid = drugs.every(
      (drug) => !isAuthPhoneRequiredForPrescribedItem(drug) || drug.phoneApprovalAuthorityNumber
    )

    if (!isAllDrugsPhoneAuthValid) {
      return {
        isValid: false,
        error:
          'Please make sure you provide the phone approval authority number when the selected drug requires a phone Authority',
      }
    }

    return {
      isValid: true,
      error: null,
    }
  }

  return {
    validateDrugs,
  }
}
